export type MetricType = 'weight' | 'distance' | 'workouts';

// Conversion constants (matching v2 implementation)
const LBS_TO_KG = 0.453592;
const KG_TO_LBS = 2.20462;

/**
 * Convert weight from lbs to kg
 */
export const convertToMetric = (
  value: number,
  type: MetricType = 'weight',
  precision?: number
) => {
  let result: number;
  switch (type) {
    case 'weight':
      result = value * LBS_TO_KG;
      break;
    case 'distance':
      result = value / 3281;
      break;
    default:
      result = value;
  }

  if (precision !== undefined) {
    const accuracy = Math.pow(10, precision);
    return Math.round(result * accuracy) / accuracy;
  }

  return result;
};

/**
 * Convert weight from kg to lbs
 */
export const convertToStandard = (
  value: number,
  type: MetricType = 'weight',
  precision?: number
) => {
  let result: number;
  
  switch (type) {
    case 'weight': // kg to lbs
      result = value * KG_TO_LBS;
      break;
    case 'distance': // km to ft
      result = value * 3281;
      break;
    default:
      result = value;
  }

  if (precision !== undefined) {
    const accuracy = Math.pow(10, precision);
    return Math.round(result * accuracy) / accuracy;
  }

  return result;
};

export const convertFeetToMiles = (value: number) => {
  return value / 5280;
};

export const unitLabel = (standard: string, usesMetric?: boolean) => {
  if (standard == 'lbs') return usesMetric ? 'kg' : 'lbs';
  if (standard == 'miles') return usesMetric ? 'km' : 'mi';
  if (standard == 'mi') return usesMetric ? 'km' : 'mi';
  if (standard == 'mis') return usesMetric ? 'kms' : 'mi';
  if (standard == 'yards') return usesMetric ? 'm' : 'yds';
  if (standard == 'feet') return usesMetric ? 'm' : 'ft';

  return standard;
};

// Equivalent of Ember's convert-metric helper
// params: [value, type, usesMetric, decimalPlaces, isMetric]
// type: 'lbs' | 'mi' | 'miles' | 'yards' | 'feet'
export const convertMetric = (
  value: number | null | undefined,
  type: string,
  usesMetric?: boolean,
  decimalPlaces?: number,
  isMetric?: boolean
): number | string => {
  if (value == null || Number.isNaN(value as number)) {
    return '--';
  }

  const v = Number(value);

  // Weight conversion (based on MetricConvertable.convertWeight)
  if (type === 'lbs') {
    const precision = decimalPlaces ?? 1;
    const accuracy = Math.pow(10, precision) || 1;

    if (isMetric && !usesMetric) {
      // stored in metric, user wants standard: kg -> lbs
      return Math.round(v * KG_TO_LBS * accuracy) / accuracy;
    }
    if (!isMetric && usesMetric) {
      // stored in standard, user wants metric: lbs -> kg
      return Math.round(v * LBS_TO_KG * accuracy) / accuracy;
    }

    return v;
  }

  // Distance conversion (based on MetricConvertable.convertDistance with toFeet = false)
  if (type === 'mi' || type === 'miles' || type === 'yards' || type === 'feet') {
    const precision = decimalPlaces || 2;
    const accuracy = Math.pow(10, precision) || 1;

    // Here value is assumed to be in feet (legacy behavior)
    if (usesMetric) {
      if (type === 'miles' || type === 'mi') {
        // feet -> km
        return Math.round((v / 3280.84) * accuracy) / accuracy;
      }
      if (type === 'yards' || type === 'feet') {
        // feet -> meters
        return Math.round((v / 3.28084) * accuracy) / accuracy;
      }
    } else {
      if (type === 'miles' || type === 'mi') {
        // feet -> miles
        return Math.round((v / 5280) * accuracy) / accuracy;
      }
      if (type === 'yards') {
        // feet -> yards
        return Math.round((v / 3) * accuracy) / accuracy;
      }
      if (type === 'feet') {
        // feet -> feet
        return Math.round(v * accuracy) / accuracy;
      }
    }
  }

  // if everything fails
  return '--';
};
